import http from "@/services/http";
import { featureFlags } from "@/config/env";
import { mockManager } from "@/services/mock";

// 请求参数（参照 public_api.md 第10节）
export interface ReputationKeywordDetailParams {
  taskId: string;
  brandId: string;
  categoryId: string;
  days?: number;
  metricType: string; // 'positive' | 'negative' 或逗号分隔
  modelId?: string; // 具体模型或 'all'
}

// 新增：通过keywordId获取详情的参数接口
export interface ReputationKeywordDetailByKeywordIdParams {
  keywordId: string;
  brandId: string;
  categoryId: string;
  days?: number;
  metricType: string; // 'positive' | 'negative' 或逗号分隔
  modelId?: string; // 具体模型或 'all'
}

// 响应数据结构（参照文档）
export interface ReputationKeywordDetailData {
  taskId: string;
  questionId: string;
  question: string;
  answer: string;
  modelId: string;
  modelName: string;
  createTime: number;
  linksNum: number;
  brandId?: string;
  categoryId?: string;
  days?: number;
  metricType?: string;
}

// 新增：通过keywordId获取详情的响应数据结构
export interface ReputationKeywordDetailByKeywordIdData {
  list: ReputationKeywordDetailData[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ReputationKeywordDetailResponse {
  code: number;
  message: string;
  data: ReputationKeywordDetailData;
}

// 新增：通过keywordId获取详情的响应接口
export interface ReputationKeywordDetailByKeywordIdResponse {
  code: number;
  message: string;
  data: ReputationKeywordDetailByKeywordIdData;
}

export const reputationDetailApi = {
  async getReputationKeywordDetail(
    params: ReputationKeywordDetailParams
  ): Promise<ReputationKeywordDetailData> {
    if (featureFlags.enableMock) {
      return await mockManager.getReputationKeywordDetail(params);
    }

    try {
      const response = await http.get<ReputationKeywordDetailResponse>(
        "/data/brand/questions/keyword",
        { params }
      );
      if (!response?.data?.data) {
        // 后端未返回期望数据时，回退到 mock，保证功能可用
        return await mockManager.getReputationKeywordDetail(params);
      }
      return response.data.data;
    } catch (err) {
      // 网络或接口错误时，回退到 mock
      return await mockManager.getReputationKeywordDetail(params);
    }
  },

  // 新增：通过keywordId获取详情的方法
  async getReputationKeywordDetailByKeywordId(
    params: ReputationKeywordDetailByKeywordIdParams
  ): Promise<ReputationKeywordDetailData> {
    if (featureFlags.enableMock) {
      return await mockManager.getReputationKeywordDetailByKeywordId(params);
    }

    try {
      const response =
        await http.get<ReputationKeywordDetailByKeywordIdResponse>(
          "/data/brand/questions/answer",
          { params }
        );
      if (!response?.data?.data?.list || response.data.data.list.length === 0) {
        // 后端未返回期望数据时，回退到 mock，保证功能可用
        return await mockManager.getReputationKeywordDetailByKeywordId(params);
      }
      // 返回列表中的第一个元素
      return response.data.data.list[0];
    } catch (err) {
      // 网络或接口错误时，回退到 mock
      return await mockManager.getReputationKeywordDetailByKeywordId(params);
    }
  },

  // 新增：通过keywordId获取详情的方法（支持分页）
  async getReputationKeywordDetailByKeywordIdWithPagination(
    params: ReputationKeywordDetailByKeywordIdParams & {
      page?: number;
      pageSize?: number;
    }
  ): Promise<ReputationKeywordDetailByKeywordIdData> {
    if (featureFlags.enableMock) {
      return await mockManager.getReputationKeywordDetailByKeywordIdWithPagination(
        params
      );
    }

    try {
      const response =
        await http.get<ReputationKeywordDetailByKeywordIdResponse>(
          "/data/brand/questions/answer",
          { params }
        );
      if (!response?.data?.data?.list || response.data.data.list.length === 0) {
        // 后端未返回期望数据时，回退到 mock，保证功能可用
        return await mockManager.getReputationKeywordDetailByKeywordIdWithPagination(
          params
        );
      }
      // 返回完整的分页数据
      return response.data.data;
    } catch (err) {
      // 网络或接口错误时，回退到 mock
      return await mockManager.getReputationKeywordDetailByKeywordIdWithPagination(
        params
      );
    }
  },
};

export default reputationDetailApi;
